/**
 * AddClientSheet Component (Story 3.2)
 *
 * Modal dialog for creating a new client with Framer Motion animations.
 */

import { useState, useRef, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button, Input } from "@provenleap/ui";
import { motion, AnimatePresence } from "motion/react";
import { apiPost } from "../../lib/apiClient";
import { useUpgradeGuard } from "../../hooks/useUpgradeGuard";
import { usePermission, Actions } from "../../hooks/usePermission";
import { IndustryPicker } from "./IndustryPicker";
import {
  OverageAddConfirmDialog,
  type OverageClientDraft,
} from "./OverageAddConfirmDialog";

interface AddClientSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientCount: number;
}

class ClientCreateProblem extends Error {
  constructor(
    message: string,
    readonly type?: string,
  ) {
    super(message);
    this.name = "ClientCreateProblem";
  }
}

function isProblemType(error: unknown, slug: string): boolean {
  return (
    error instanceof ClientCreateProblem &&
    typeof error.type === "string" &&
    error.type.endsWith(`/${slug}`)
  );
}

function AddClientSheet({
  open,
  onOpenChange,
  clientCount,
}: AddClientSheetProps) {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canManageBilling = hasPermission(Actions.MANAGE_BILLING);

  // Form state
  const [name, setName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [industry, setIndustry] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [overageDraft, setOverageDraft] = useState<OverageClientDraft | null>(
    null,
  );
  const [overageDialogOpen, setOverageDialogOpen] = useState(false);

  // Toast state
  const [toast, setToast] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const toastTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Upgrade guard
  const { guardAction, UpgradeModal, showUpgradeModal } = useUpgradeGuard({
    feature: "clientLimit",
    currentCount: clientCount,
    // The server knows the per-tenant overage rollout flag; the static plan
    // table does not. Always let the ordinary endpoint make the first decision.
    serverAuthoritative: true,
  });

  // Clear toast timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  function showToast(type: "success" | "error", message: string) {
    setToast({ type, message });
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    toastTimeoutRef.current = setTimeout(() => setToast(null), 3000);
  }

  function resetForm() {
    setName("");
    setContactName("");
    setContactEmail("");
    setIndustry("");
    setErrors({});
  }

  function resetOverageFlow() {
    setOverageDraft(null);
    setOverageDialogOpen(false);
  }

  function handleOpenChange(newOpen: boolean) {
    if (!newOpen) {
      resetForm();
      resetOverageFlow();
    }
    onOpenChange(newOpen);
  }

  function beginOverageConfirmation(draft: OverageClientDraft): void {
    if (!canManageBilling) {
      showToast(
        "error",
        "An account owner must review and approve the overage before this client can be added.",
      );
      return;
    }
    setOverageDraft(draft);
    setOverageDialogOpen(true);
  }

  function handleOverageDialogOpenChange(next: boolean): void {
    setOverageDialogOpen(next);
    if (!next) setOverageDraft(null);
  }

  function handleOverageConfirmed(): void {
    void queryClient.invalidateQueries({ queryKey: ["clients"] });
    resetForm();
    resetOverageFlow();
    onOpenChange(false);
    showToast("success", "Client created");
  }

  // Validation
  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    const trimmedName = name.trim();

    if (!trimmedName) {
      newErrors.name = "Client name is required";
    } else if (trimmedName.length > 100) {
      newErrors.name = "Client name must be 100 characters or less";
    }

    if (
      contactEmail.trim() &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail.trim())
    ) {
      newErrors.contactEmail = "Invalid email format";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  // Mutation
  const createClient = useMutation({
    mutationFn: async (data: {
      name: string;
      contactName?: string;
      contactEmail?: string;
      industry?: string;
    }) => {
      const res = await apiPost("/api/clients", data);
      if (!res.ok) {
        const err = await res.json();
        const detail =
          err.errors?.[0]?.message ?? err.detail ?? "Failed to create client";
        throw new ClientCreateProblem(detail, err.type);
      }
      return res.json();
    },
    onMutate: async (newClient) => {
      // Optimistic update
      await queryClient.cancelQueries({ queryKey: ["clients"] });
      const previousClients = queryClient.getQueryData(["clients"]);
      queryClient.setQueryData(
        ["clients"],
        (old: Array<Record<string, unknown>> | undefined) => [
          {
            id: `temp-${Date.now()}`,
            name: newClient.name,
            contactName: newClient.contactName || null,
            contactEmail: newClient.contactEmail || null,
            industry: newClient.industry || null,
            businessGoals: null,
            notes: null,
            logoUrl: null,
            archivedAt: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          ...(old || []),
        ],
      );
      return { previousClients };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      resetForm();
      onOpenChange(false);
      showToast("success", "Client created");
    },
    onError: (err: Error, _vars, context) => {
      // Rollback optimistic update
      if (context) {
        queryClient.setQueryData(["clients"], context.previousClients);
      }
      if (isProblemType(err, "overage-confirmation-required")) {
        beginOverageConfirmation(_vars);
        return;
      }
      if (isProblemType(err, "upgrade-required")) {
        showUpgradeModal();
        return;
      }
      showToast("error", err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    guardAction(async () => {
      const data: {
        name: string;
        contactName?: string;
        contactEmail?: string;
        industry?: string;
      } = { name: name.trim() };

      if (contactName.trim()) data.contactName = contactName.trim();
      if (contactEmail.trim()) data.contactEmail = contactEmail.trim();
      if (industry) data.industry = industry;

      createClient.mutate(data);
    });
  }

  return (
    <>
      <DialogPrimitive.Root
        open={open && !overageDialogOpen}
        onOpenChange={handleOpenChange}
      >
        <AnimatePresence>
          {open && !overageDialogOpen && (
            <DialogPrimitive.Portal forceMount>
              {/* Overlay */}
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              {/* Content */}
              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg max-h-[85vh] overflow-y-auto outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Add New Client
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Create a new client to manage their reports and
                      integrations.
                    </DialogPrimitive.Description>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                    {/* Client Name (required) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.05 }}
                    >
                      <label
                        htmlFor="client-name"
                        className="text-sm font-medium"
                      >
                        Client Name <span className="text-destructive">*</span>
                        {errors.name && (
                          <span
                            className="text-sm text-destructive font-normal ml-1"
                            role="alert"
                          >
                            {errors.name}
                          </span>
                        )}
                      </label>
                      <Input
                        id="client-name"
                        value={name}
                        onChange={(e) => {
                          setName(e.target.value);
                          if (errors.name)
                            setErrors((prev) => ({ ...prev, name: "" }));
                        }}
                        placeholder="e.g. Acme Corp"
                        className={`rounded-lg ${errors.name ? "border-destructive" : ""}`}
                        maxLength={100}
                      />
                    </motion.div>

                    {/* Contact Name (optional) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 }}
                    >
                      <label
                        htmlFor="contact-name"
                        className="text-sm font-medium"
                      >
                        Contact Name
                      </label>
                      <Input
                        id="contact-name"
                        value={contactName}
                        onChange={(e) => setContactName(e.target.value)}
                        placeholder="e.g. John Doe"
                        maxLength={255}
                        className="rounded-lg"
                      />
                    </motion.div>

                    {/* Contact Email (optional) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.15 }}
                    >
                      <label
                        htmlFor="contact-email"
                        className="text-sm font-medium"
                      >
                        Contact Email
                      </label>
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => {
                          setContactEmail(e.target.value);
                          if (errors.contactEmail)
                            setErrors((prev) => ({
                              ...prev,
                              contactEmail: "",
                            }));
                        }}
                        onBlur={() => {
                          if (
                            contactEmail.trim() &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                              contactEmail.trim(),
                            )
                          ) {
                            setErrors((prev) => ({
                              ...prev,
                              contactEmail: "Invalid email format",
                            }));
                          }
                        }}
                        placeholder="e.g. john@acme.com"
                        className={`rounded-lg ${errors.contactEmail ? "border-destructive" : ""}`}
                      />
                      {errors.contactEmail && (
                        <p className="text-sm text-destructive" role="alert">
                          {errors.contactEmail}
                        </p>
                      )}
                    </motion.div>

                    {/* Industry (optional dropdown) */}
                    <motion.div
                      className="space-y-2"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      <label htmlFor="industry" className="text-sm font-medium">
                        Industry
                      </label>
                      <IndustryPicker
                        id="industry"
                        value={industry}
                        onChange={setIndustry}
                      />
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25 }}
                    >
                      <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                        <Button
                          type="button"
                          variant="ghost"
                          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                          style={{ border: "0.5px solid hsl(var(--input))" }}
                          onClick={() => handleOpenChange(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="brand"
                          className="rounded-xl cursor-pointer"
                          disabled={!name.trim() || createClient.isPending}
                        >
                          {createClient.isPending
                            ? "Creating..."
                            : "Create Client"}
                        </Button>
                      </div>
                    </motion.div>
                  </form>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      {/* Toast notification */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className={`fixed bottom-4 right-4 z-50 rounded-xl px-4 py-3 text-sm shadow-lg ${
              toast.type === "success"
                ? "bg-green-50 border border-green-200 text-green-800"
                : "bg-destructive/10 border border-destructive/20 text-destructive"
            }`}
            role={toast.type === "error" ? "alert" : "status"}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      {overageDraft && (
        <OverageAddConfirmDialog
          open={overageDialogOpen}
          onOpenChange={handleOverageDialogOpenChange}
          draft={overageDraft}
          onConfirmed={handleOverageConfirmed}
        />
      )}

      {UpgradeModal}
    </>
  );
}

export { AddClientSheet };
